import React, { useState, useEffect, useMemo } from 'react';
import {
    Checkbox,
    MenuItem,
    FormControl,
    Menu,
    Box,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Collapse,
    Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useAppState } from 'store/Provider';
import { selectFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';

// Update types to match your deliverable_status1 JSON
type StatusOption = {
    id: number;
    name: string;
};

type MockOptions = Record<string, StatusOption[]>;

interface CollapsibleStatusFilterProps {
    name: string;
    apiKeyname: string;
    label: string;
    disabled?: boolean;
    mockOptions: MockOptions; // This will receive deliverable_status1
    value: string[] | 'ALL';
    onChange: (selectedValues: string[] | 'ALL') => void;
    clearPerticularFilters: (apiKeyname: string) => void;
    setDisplaySelectedStatus: React.Dispatch<React.SetStateAction<string>>;
}

const CollapsibleStatusFilter: React.FC<CollapsibleStatusFilterProps> = ({
    name,
    apiKeyname,
    label,
    mockOptions,
    value,
    onChange,
    clearPerticularFilters,
    setDisplaySelectedStatus,
}) => {
    const { dispatch } = useAppState();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [search, setSearch] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    // Track checked state using ID or Name. Using ID is safer for APIs.
    const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
    const stop = (e: React.MouseEvent) => e.stopPropagation();

    const menuOpen = Boolean(anchorEl);

    // Flatten all IDs for easy "Select All" logic
    const allItemIds = useMemo(
        () => Object.values(mockOptions || {}).flatMap((group) => group.map((item) => item.id)),
        [mockOptions]
    );

    useEffect(() => {
        const initialChecked: Record<number, boolean> = {};

        if (value === 'ALL') {
            allItemIds.forEach((id) => {
                initialChecked[id] = true;
            });
            setCheckedItems(initialChecked);
            setIsSelectAllChecked(true);
        } else {
            const selectedArray = Array.isArray(value) ? value.map(Number) : [];
            allItemIds.forEach((id) => {
                initialChecked[id] = selectedArray.includes(id);
            });
            setCheckedItems(initialChecked);
            setIsSelectAllChecked(selectedArray.length === allItemIds.length && allItemIds.length > 0);
        }
    }, [value, allItemIds]);

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => {
        setSearch('');
        setAnchorEl(null);
    };

    const handleItemToggle = (id: number, checked: boolean) => {
        const updated = { ...checkedItems, [id]: checked };
        setCheckedItems(updated);

        const allChecked = allItemIds.every((itemId) => updated[itemId]);
        setIsSelectAllChecked(allChecked);
    };

    const handleGroupToggle = (groupItems: StatusOption[], checked: boolean) => {
        const updated = { ...checkedItems };
        groupItems.forEach((item) => {
            updated[item.id] = checked;
        });
        setCheckedItems(updated);

        const allChecked = allItemIds.every((itemId) => updated[itemId]);
        setIsSelectAllChecked(allChecked);
    };

    const handleApply = () => {
        const selectedIds = Object.keys(checkedItems)
            .filter((id) => checkedItems[Number(id)])
            .map(String);

        if (selectedIds.length === allItemIds.length && allItemIds.length > 0) {
            dispatch(selectFilterAction({ [apiKeyname]: ['ALL'] }));
            onChange('ALL');
            setDisplaySelectedStatus('ALL');
        } else {
            dispatch(selectFilterAction({ [apiKeyname]: selectedIds }));
            onChange(selectedIds);
            // Create a display string (e.g., "Submitted, DM Refused")
            const displayNames = Object.values(mockOptions)
                .flatMap((g) => g)
                .filter((i) => checkedItems[i.id])
                .map((i) => i.name);
            setDisplaySelectedStatus(displayNames.join(', '));
        }
        handleClose();
    };

    const handleClear = () => {
        setCheckedItems({});
        setIsSelectAllChecked(false);
        onChange([]);
        clearPerticularFilters(apiKeyname);
        dispatch(selectFilterRemoveAction({ [name]: 'ALL' }));
        setDisplaySelectedStatus('');
        handleClose();
    };

    // Display value for the TextField
    const getDisplayValue = () => {
        if (value === 'ALL') return 'ALL';

        // 1. Get all IDs that are currently checked
        const selectedIds = Object.entries(checkedItems)
            .filter(([_, isChecked]) => isChecked)
            .map(([id]) => Number(id));

        if (selectedIds.length === 0) return '';

        // 2. Find the names corresponding to those IDs
        const selectedNames = Object.values(mockOptions)
            .flat() // Flatten the grouped arrays into one list
            .filter((item) => selectedIds.includes(item.id))
            .map((item) => item.name);

        // 3. Join them with a comma
        return selectedNames.join(', ');
    };

    return (
        <Box sx={{ mb: 2 }}>
            <FormControl fullWidth>
                <Typography
                    component="label"
                    htmlFor={`collapsible-country-filter-${name}`}
                    sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'primary.main',
                        mb: 0.5,
                        display: 'block',
                    }}
                >
                    {label}
                </Typography>

                <TextField
                    value={getDisplayValue()}
                    onClick={handleOpen}
                    readOnly
                    fullWidth
                    size="small"
                    placeholder="Select Status..."
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                {getDisplayValue() && value !== 'ALL' && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleClear();
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <ArrowDropDownIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </FormControl>

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: anchorEl ? anchorEl.clientWidth : 320,
                        maxHeight: 450,
                        mt: 0.5,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                {/* Search */}
                <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search status..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    />
                </Box>

                {/* Select All */}
                <MenuItem sx={{ py: 1, px: 2, borderBottom: '1px solid #e0e0e0' }} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        size="small"
                        checked={isSelectAllChecked}
                        onChange={(e) => {
                            const { checked } = e.target; // Use destructuring here
                            const updated: Record<number, boolean> = {};

                            allItemIds.forEach((id) => {
                                updated[id] = checked;
                            });
                            setCheckedItems(updated);
                            setIsSelectAllChecked(checked);
                        }}
                        sx={{ p: 0.5, mr: 1 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        ALL
                    </Typography>
                </MenuItem>

                {/* Status Groups */}
                <Box sx={{ overflowY: 'auto', flexGrow: 1, maxHeight: 250 }}>
                    {Object.entries(mockOptions).map(([groupName, items]) => {
                        const filteredItems = items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));
                        if (filteredItems.length === 0) return null;

                        const groupChecked = filteredItems.every((i) => checkedItems[i.id]);
                        const groupSomeChecked = filteredItems.some((i) => checkedItems[i.id]);
                        const isExpanded = expandedGroups[groupName] || search.length > 0;

                        return (
                            <Box key={groupName}>
                                <Box
                                    sx={{
                                        py: 1.2,
                                        px: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        minHeight: 50,
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                    onClick={() => setExpandedGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }))}
                                >
                                    <IconButton size="small" sx={{ p: 0, width: 24, height: 24 }}>
                                        {isExpanded ? <ExpandMoreIcon /> : <KeyboardArrowRightIcon />}</IconButton>
                                    <Checkbox
                                        size="small"
                                        checked={groupChecked}
                                        indeterminate={groupSomeChecked && !groupChecked}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleGroupToggle(filteredItems, e.target.checked);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ p: 0.5 }}
                                    />
                                    <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                                        {groupName}
                                    </Typography>
                                </Box>

                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ pl: 6, pr: 1.5, py: 0.5, backgroundColor: '#fafafa' }}>
                                        {filteredItems.map((item) => (
                                            <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                                                <Checkbox
                                                    size="small"
                                                    checked={checkedItems[item.id] || false}
                                                    onChange={(e) => handleItemToggle(item.id, e.target.checked)}
                                                />
                                                <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>{item.name}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })}
                </Box>

                {/* Actions */}
                <Box
                    onMouseDown={stop}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        p: 1,
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: '#fafafa',
                        flexShrink: 0,
                    }}
                >
                    <Button size="small" onClick={handleClose} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={handleApply} sx={{ textTransform: 'none' }}>
                        Apply
                    </Button>
                </Box>
            </Menu>
        </Box>
    );
};

export default CollapsibleStatusFilter;
